import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { MessageSquare, X } from 'lucide-react';
import InternalCommentsThread from './InternalCommentsThread';

/**
 * CommentsDrawer — persistent right-side slide-out for the internal
 * comment thread on a lender submission pack. The floating toggle stays
 * visible from every section (broker notes, docs, checklist, QC, sign-off,
 * post-lodgement Command Centre, MIRs, timeline, etc.) so the team can
 * chat without losing their place in the pack editor.
 *
 * - Slack/Intercom-style right drawer, ~420px wide.
 * - Unread badge tracks comments created since the user last opened the
 *   drawer (stored per-pack in localStorage).
 * - Enter submits, Shift+Enter for a new line (delegated to the thread).
 * - Escape closes the drawer.
 */
export default function CommentsDrawer({ pack, onPackChange }) {
  const [open, setOpen] = useState(false);
  const packId = pack?.id;
  const storageKey = useMemo(() => (packId ? `pack-comments-lastseen-${packId}` : null), [packId]);

  const comments = pack?.internal_comments || [];

  // Last-seen ISO timestamp per pack (persists across reloads/section changes)
  const [lastSeen, setLastSeen] = useState(() => {
    if (typeof window === 'undefined' || !storageKey) return null;
    return window.localStorage.getItem(storageKey);
  });

  // Refresh last-seen when the pack changes (e.g. navigating between packs)
  useEffect(() => {
    if (!storageKey) return;
    setLastSeen(window.localStorage.getItem(storageKey));
  }, [storageKey]);

  const unreadCount = useMemo(() => {
    if (!comments.length) return 0;
    if (!lastSeen) return comments.length;
    const cutoff = new Date(lastSeen).getTime();
    return comments.filter(c => {
      const t = c?.created_at ? new Date(c.created_at).getTime() : 0;
      return t > cutoff;
    }).length;
  }, [comments, lastSeen]);

  const markSeen = useCallback(() => {
    if (!storageKey) return;
    const now = new Date().toISOString();
    window.localStorage.setItem(storageKey, now);
    setLastSeen(now);
  }, [storageKey]);

  const openDrawer = useCallback(() => {
    setOpen(true);
    markSeen();
  }, [markSeen]);

  const closeDrawer = useCallback(() => {
    setOpen(false);
    markSeen();
  }, [markSeen]);

  // Escape to close, `c` shortcut to toggle (when not typing in another field)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && open) {
        closeDrawer();
        return;
      }
      const target = e.target;
      const isTyping = target && (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      );
      if (!isTyping && (e.key === 'c' || e.key === 'C') && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        setOpen(v => {
          const next = !v;
          if (next) markSeen();
          return next;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeDrawer, markSeen]);

  // Mark seen while the drawer is open and new comments arrive
  useEffect(() => {
    if (open) markSeen();
  }, [open, comments.length, markSeen]);

  if (!pack) return null;

  return (
    <>
      {/* Floating toggle button — always visible bottom-right */}
      {!open && (
        <button
          type="button"
          onClick={openDrawer}
          data-testid="comments-drawer-toggle"
          aria-label={`Open internal comments (${unreadCount} unread)`}
          className="fixed bottom-6 right-6 z-40 group flex items-center gap-2 rounded-full shadow-2xl transition-all hover:scale-105 active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            color: '#ffffff',
            padding: '14px 18px',
            boxShadow: '0 10px 30px -8px rgba(220,38,38,0.55), 0 4px 10px -4px rgba(0,0,0,0.2)',
          }}
        >
          <MessageSquare className="w-5 h-5" />
          <span className="text-[12px] font-black uppercase tracking-widest hidden sm:inline">Comments</span>
          <span
            className="min-w-[22px] h-[22px] rounded-full inline-flex items-center justify-center text-[11px] font-black tabular-nums px-1.5"
            style={{
              background: unreadCount > 0 ? '#facc15' : 'rgba(255,255,255,0.25)',
              color: unreadCount > 0 ? '#78350f' : '#ffffff',
              border: unreadCount > 0 ? '2px solid #ffffff' : 'none',
            }}
            data-testid="comments-drawer-badge"
          >
            {unreadCount > 0 ? unreadCount : comments.length}
          </span>
        </button>
      )}

      {/* Backdrop */}
      {open && (
        <div
          role="presentation"
          onClick={closeDrawer}
          className="fixed inset-0 z-40 transition-opacity"
          style={{ background: 'rgba(15,23,42,0.45)' }}
          data-testid="comments-drawer-backdrop"
        />
      )}

      {/* Right slide-out drawer */}
      <aside
        aria-hidden={!open}
        data-testid="comments-drawer"
        className="fixed top-0 right-0 z-50 h-screen w-full sm:w-[420px] flex flex-col shadow-2xl transition-transform duration-300 ease-out"
        style={{
          background: '#ffffff',
          borderLeft: '1px solid #e2e8f0',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
        }}
      >
        {/* Drawer header */}
        <div
          className="flex items-center justify-between px-4 py-3 border-b shrink-0"
          style={{ background: '#fef2f2', borderColor: '#fecaca' }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: '#dc2626' }}
            >
              <MessageSquare className="w-4 h-4" style={{ color: '#ffffff' }} />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-black leading-tight truncate" style={{ color: '#7f1d1d' }}>
                Internal comments
              </div>
              <div className="text-[10.5px] leading-tight truncate" style={{ color: '#991b1b' }}>
                {pack.client_name} · {pack.lender} · shared across every section
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            aria-label="Close comments drawer"
            className="p-1.5 rounded-md hover:bg-white/60 transition-colors"
            data-testid="comments-drawer-close"
          >
            <X className="w-4 h-4" style={{ color: '#7f1d1d' }} />
          </button>
        </div>

        {/* Thread body — fills remaining height */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <InternalCommentsThread
            pack={pack}
            onPackChange={onPackChange}
            variant="drawer"
          />
        </div>
      </aside>
    </>
  );
}
